import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Ban, Download, Link as LinkIcon, Mail, MessageCircle, PackageCheck, Pencil, PenLine, Printer, RotateCcw, Send, UserRound } from "lucide-react";
import { api, DOC_LABELS, money, openDownload, shopDateTime } from "@/lib/api";
import { AuthedImage } from "@/components/AuthedImage";
import { deliverFile } from "@/lib/pdfFiling";
import { SignaturePad } from "@/components/SignaturePad";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LoanLedger } from "@/components/LoanLedger";
import { formatPhone } from "@/lib/phone";

const NUMBER_LABELS = {
  tax_invoice: "INVOICE NO:",
  gold_bullion: "INVOICE NO:",
  loan: "LOAN NO:",
  buying_contract: "BUYING CONT NO:",
};

const TITLES = {
  tax_invoice: "TAX INVOICE",
  gold_bullion: "TAX INVOICE - GOLD BULLION",
  loan: "LOAN AGREEMENT",
  buying_contract: "BUYING CONTRACT",
};

const ACK = {
  tax_invoice:
    "HAVE READ AND UNDERSTOOD THE TERMS & CONDITIONS. ALSO CHECK THE JEWELLERY GOODS GIVEN BY DHUNNA JEWELLERS ARE PERFECT CONDITION AND ALL ITEMS NOT CONTAIN ANY DAMAGE OR DEFECT.",
  gold_bullion:
    "HAVE READ AND UNDERSTOOD THE TERMS & CONDITIONS. ALSO CHECK THE GOLD BULLION 9999 GOODS GIVEN BY DHUNNA JEWELLERS ARE PERFECT CONDITION AND DO NOT CONTAIN ANY DAMAGE OR DEFECT.",
  loan:
    "ACKNOWLEDGE AND AGREE THAT THE JEWELLERY PLEDGED UNDER THIS LOAN AGREEMENT IS PROVIDED AS SECURITY FOR THE REPAYMENT OF THE LOAN TOGETHER WITH ALL ACCRUED INTEREST, FEES AND COSTS, AND HAVE READ AND UNDERSTOOD THE TERMS & CONDITIONS ON THE REVERSE PAGE.",
  buying_contract:
    "CONFIRM THAT I/WE ARE THE OWNER(S) OF THE GOLD JEWELLERY OR HAVE THE OWNER'S FULL AUTHORITY TO SELL AND AUTHORISE THE MELTING OF THE GOLD JEWELLERY. WARNING: PROVIDING FALSE OR MISLEADING INFORMATION IS A CRIMINAL OFFENCE.",
};

const KARATS = ["24K", "22K", "21K", "18K", "14K"];

const TRS_REASON = "Tourist Refund Scheme (TRS) — name must match passport";

const Brand = ({ abn }) => (
  <div>
    <img src="/brand/header.png" alt="Dhunna Jewellers" className="w-full" />
    <div className="mt-0.5 text-right font-invoice text-[11px] font-bold">ABN: {abn}</div>
  </div>
);

const StoreFooter = ({ entity, footer }) => (
  <div className="mt-4" data-testid="print-footer">
    <div className="text-center text-[13px] font-bold uppercase tracking-wide text-[#C08A2E]">
      {footer?.tagline || "THANK YOU FOR YOUR BUSINESS"}
    </div>
    <div className="mt-2 flex flex-wrap justify-between gap-3">
      {(footer?.stores || []).map((s, i) => (
        <div key={i} className="flex-1 text-center" data-testid={`footer-store-${i}`}>
          <div className="mx-auto w-fit bg-[#C08A2E] px-3 py-0.5 text-[9px] font-bold uppercase text-white">
            {s.title}
          </div>
          <div className="mt-1 text-[9px] leading-snug">{s.phone}</div>
          <div className="text-[9px] leading-snug">{s.address}</div>
          <div className="text-[9px] leading-snug">
            {s.contact_label} {s.contact_number}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-2 text-center font-invoice text-[10px] font-bold text-[#C08A2E]">
      T&amp;Cs APPLY ON REVERSE PAGE | {entity}
    </div>
  </div>
);

// Customer Name_Document Type_Number — the shop's filing standard.
const DOC_FILE_TITLES = {
  tax_invoice: "Tax Invoice",
  gold_bullion: "Gold Bullion Invoice",
  loan: "Loan Agreement",
  buying_contract: "Buying Contract",
};
// Each document type gets its own subfolder inside the folder the shop chose, named
// to match the filing standard above.
const docSubfolder = (doc) => DOC_FILE_TITLES[doc.doc_type] || "Documents";
const pdfFileName = (doc) =>
  `${[doc.customer_name, DOC_FILE_TITLES[doc.doc_type] || "Document", doc.doc_number]
    .filter(Boolean)
    .join("_")
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")}.pdf`;

const PORTAL_URL = (process.env.REACT_APP_CUSTOMER_PORTAL_URL
  || `${window.location.origin}/invoices`).replace(/\/$/, "");
const portalLine = ` You can see all your invoices any time at ${PORTAL_URL}.`;

export default function DocumentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [doc, setDoc] = useState(null);
  const [terms, setTerms] = useState({});
  const [branches, setBranches] = useState({});
  const [footer, setFooter] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emails, setEmails] = useState([]);
  const [pdfLink, setPdfLink] = useState("");
  // The preview shows the real printed document, page by page, so what is on
  // screen cannot drift away from what the printer produces.
  const [pdfPreview, setPdfPreview] = useState("");
  const [pages, setPages] = useState([]);
  const [pdfBroken, setPdfBroken] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const pdfFrame = useRef(null);
  const [payFull, setPayFull] = useState(false);
  const [roundOff, setRoundOff] = useState(false);
  const [roundAmt, setRoundAmt] = useState("");
  const [payMethod, setPayMethod] = useState("CASH");
  const [payAmount, setPayAmount] = useState("");
  const [feeRates, setFeeRates] = useState({});
  const [signOpen, setSignOpen] = useState(false);
  const [signSaving, setSignSaving] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renamePhone, setRenamePhone] = useState("");
  const [renameAddress, setRenameAddress] = useState("");
  const [renameReason, setRenameReason] = useState(TRS_REASON);
  const [renaming, setRenaming] = useState(false);

  const saveInvoiceName = async () => {
    setRenaming(true);
    try {
      const { data } = await api.put(`/documents/${id}/customer-name`, {
        new_name: renameName,
        new_phone: renamePhone,
        new_address: renameAddress,
        reason: renameReason,
      });
      setDoc(data);
      setRenameOpen(false);
      toast.success("Details changed on this record only — the customer account is unchanged");
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not change the details"
      );
    } finally {
      setRenaming(false);
    }
  };

  const saveSignature = async (image) => {
    setSignSaving(true);
    try {
      await api.post(`/documents/${id}/signature`, { image });
      const { data } = await api.get(`/documents/${id}`);
      setDoc(data);
      setSignOpen(false);
      toast.success("Signature captured");
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not save the signature"
      );
    } finally {
      setSignSaving(false);
    }
  };

  const requestSignature = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/signature-request`);
      toast.success(`Signing link emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not send the signing link"
      );
    }
  };

  // Must agree with Python's round(x, 2) on the server to the cent — see the full note
  // on round2 in DocumentForm.jsx. Keep the two identical.
  const round2 = (n) => {
    const x = Number(n) || 0;
    if (!Number.isFinite(x)) return 0;
    const [intPart, frac = ""] = Math.abs(x).toFixed(20).split(".");
    const rest = frac.slice(2).replace(/0+$/, "");
    let cents = Number(intPart) * 100 + Number(frac.slice(0, 2).padEnd(2, "0"));
    if (rest === "5") cents += cents % 2 === 1 ? 1 : 0;
    else if (rest.startsWith("5") || rest > "5") cents += 1;
    return (x < 0 ? -cents : cents) / 100;
  };

  const feeParts = (method) => {
    const r = feeRates[method];
    if (!r) return { pct: 0, fixed: 0 };
    return typeof r === "object"
      ? { pct: Number(r.pct || 0) / 100, fixed: Number(r.fixed || 0) }
      : { pct: Number(r || 0) / 100, fixed: 0 };
  };

  // What the shop needs to take to clear the invoice. The provider's fee is added and
  // kept at their end, so it never forms part of this figure.
  const payPlan = () => {
    const outstanding = round2(doc.balance || 0);
    const { pct, fixed } = feeParts(payMethod);
    const typed = Number(payAmount);
    const charge = payAmount && typed > 0 ? round2(typed) : outstanding;
    const fee = pct || fixed ? round2(charge * pct + fixed) : 0;
    return { outstanding, charge, fee, settle: outstanding,
             left: round2(outstanding - charge) };
  };

  const applyRoundOff = async () => {
    const amt = roundAmt ? round2(Number(roundAmt)) : round2(doc.balance || 0);
    if (!(amt > 0)) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    if (
      amt > 9.99 &&
      !window.confirm(
        `Round off ${money(amt)}? That is above the usual $9.99 — apply it anyway?`
      )
    )
      return;
    try {
      const { data } = await api.post(`/documents/${id}/round-off`, { amount: amt });
      setDoc(data);
      setRoundOff(false);
      setRoundAmt("");
      toast.success(`Rounded off ${money(amt)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const settleBalance = async () => {
    const amount = Number(payAmount);
    const full = !payAmount || Math.abs(amount - (doc.balance || 0)) < 0.005;
    try {
      if (full) {
        const { data } = await api.post(`/documents/${id}/pay-full`, { method: payMethod });
        setDoc(data);
      } else {
        if (!(amount > 0)) {
          toast.error("Enter an amount greater than zero");
          return;
        }
        const phone = (doc.customer_phone || "").replace(/\D/g, "");
        await api.post(`/customers/${phone}/entries`, {
          kind: "payment",
          amount,
          method: payMethod,
          doc_id: id,
        });
        const { data } = await api.get(`/documents/${id}`);
        setDoc(data);
      }
      setPayFull(false);
      setPayAmount("");
      toast.success(`Payment by ${payMethod} recorded`);
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not record that payment"
      );
    }
  };

  useEffect(() => {
    api.get(`/documents/${id}`).then(({ data }) => setDoc(data)).catch(() => toast.error("Could not load this record"));
    api.get("/settings/terms").then(({ data }) => setTerms(data)).catch(() => setTerms({}));
    api.get("/settings/branches").then(({ data }) => setBranches(data)).catch(() => setBranches({}));
    api.get("/settings/footer").then(({ data }) => setFooter(data)).catch(() => setFooter(null));
    api.get("/settings/platform-fees").then(({ data }) => setFeeRates(data)).catch(() => setFeeRates({}));
    api.get(`/documents/${id}/emails`).then(({ data }) => setEmails(data)).catch(() => setEmails([]));
  }, [id]);

  // Load the printed document for the preview, and reload it whenever the record
  // changes (a signature, a payment, an amendment).
  useEffect(() => {
    if (!doc) return undefined;
    const urls = [];
    let cancelled = false;

    const loadPages = async () => {
      try {
        const shots = [];
        for (let page = 1; page <= 4; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const res = await api.get(`/documents/${id}/preview`, {
            params: { page },
            responseType: "blob",
            validateStatus: (s) => s === 200 || s === 404,
          });
          if (res.status === 404) break;
          const url = URL.createObjectURL(res.data);
          urls.push(url);
          shots.push(url);
          if (cancelled) return;
          // Show page one the moment it arrives rather than waiting for the back page.
          setPages([...shots]);
          setPdfBroken(false);
          setPreviewLoading(false);
          if (Number(res.headers["x-page-count"] || 0) <= page) break;
        }
        if (cancelled) return;
        if (!shots.length) throw new Error("no pages");
      } catch {
        if (!cancelled) {
          setPdfBroken(true);
          setPreviewLoading(false);
        }
      }
    };

    api
      .get(`/documents/${id}/pdf`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        urls.push(url);
        setPdfPreview(url);
      })
      .catch(() => {});
    loadPages();

    return () => {
      cancelled = true;
      urls.forEach(URL.revokeObjectURL);
      setPages([]);
      setPdfPreview("");
      setPreviewLoading(true);
    };
  }, [id, doc]);

  // One place that decides where a PDF goes: the shop's own folder when this computer
  // has been set up, otherwise the browser's normal download.
  const deliverPdf = (blob) =>
    deliverFile(blob, docSubfolder(doc), pdfFileName(doc), toast);

  const printDoc = () => {
    const frame = pdfFrame.current;
    if (frame && pdfPreview) {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        return;
      } catch {
        window.open(pdfPreview, "_blank", "noopener");
        return;
      }
    }
    window.print();
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/documents/${id}/pdf`, { responseType: "blob" });
      await deliverPdf(res.data);
    } catch (e) {
      toast.error("PDF download failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadOffice = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/documents/${id}/pdf?office=true`, { responseType: "blob" });
      const base = pdfFileName(doc);
      const name = base.toLowerCase().endsWith(".pdf")
        ? `${base.slice(0, -4)} (office copy).pdf`
        : `${base} (office copy)`;
      await deliverFile(res.data, docSubfolder(doc), name, toast);
    } catch (e) {
      toast.error("PDF download failed");
    } finally {
      setBusy(false);
    }
  };

  // One-tap print of the internal/office copy (includes the customer ID photo). Fetches the
  // office PDF into a hidden iframe and opens the print dialog, so staff don't download-then-print.
  const printInternal = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/documents/${id}/pdf?office=true`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const frame = document.createElement("iframe");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.src = url;
      frame.onload = () => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
        } catch {
          window.open(url, "_blank", "noopener");
        }
      };
      document.body.appendChild(frame);
      setTimeout(() => {
        URL.revokeObjectURL(url);
        frame.remove();
      }, 60000);
    } catch (e) {
      toast.error("Could not open the internal copy");
    } finally {
      setBusy(false);
    }
  };

  const emailToCustomer = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/documents/${id}/email`);
      toast.success(
        data.attached
          ? `Emailed to ${data.sent_to} with the invoice PDF attached`
          : `Emailed to ${data.sent_to} with a secure 48-hour download link`
      );
      setDoc({ ...doc, emailed_to: data.sent_to });
      if (data.pdf_url) setPdfLink(data.pdf_url);
      api.get(`/documents/${id}/emails`).then(({ data: e }) => setEmails(e)).catch(() => {});
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not send the email"
      );
    } finally {
      setEmailing(false);
    }
  };

  const copyLink = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/share-link`);
      setPdfLink(data.pdf_url);
      await navigator.clipboard.writeText(data.pdf_url);
      toast.success("PDF link copied — valid for 48 hours. Paste it into WhatsApp or SMS");
    } catch (e) {
      toast.error("Could not create a share link");
    }
  };

  const sendWhatsApp = async () => {
    const phone = (doc.customer_phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("This record has no phone number");
      return;
    }
    const code = String(doc.customer_country_code || "+61").replace(/\D/g, "");
    const intl = phone.startsWith(code) ? phone : `${code}${phone.replace(/^0+/, "")}`;
    const filename = pdfFileName(doc);
    try {
      const res = await api.get(`/documents/${id}/pdf`, { responseType: "blob" });
      const file = new File([res.data], filename, { type: "application/pdf" });
      const text =
        `Hi ${doc.customer_name || "there"}, please find your ` +
        `${DOC_LABELS[doc.doc_type] || "document"} #${doc.doc_number} from Dhunna Jewellers ` +
        `attached. Thank you for your business.` + portalLine;

      // Only genuine phones/tablets should use the OS share sheet to attach the PDF.
      // A Windows touchscreen laptop also matches "(pointer: coarse)", which used to send
      // it down this path and pop the Windows "Share using" panel (Outlook/email sitting
      // right next to WhatsApp) — so staff kept landing on email. Laptops and desktops now
      // open the WhatsApp chat directly instead (see below).
      const ua = navigator.userAgent || "";
      const isMobile = navigator.userAgentData
        ? navigator.userAgentData.mobile === true
        : /Android|iPhone|iPod|iPad/i.test(ua);
      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: filename });
        return;
      }

      // Desktop: WhatsApp Web cannot take a file from a website, so file the PDF into
      // the shop's folder and open the chat with the download link ready to send.
      let link = pdfLink;
      if (!link) {
        const { data } = await api.post(`/documents/${id}/share-link`);
        link = data.pdf_url;
        setPdfLink(link);
      }
      await deliverPdf(res.data);
      const desktopText =
        `Hi ${doc.customer_name || "there"}, here is your ` +
        `${DOC_LABELS[doc.doc_type] || "document"} #${doc.doc_number} from Dhunna Jewellers: ` +
        `${link}\n\nPlease note: this download link only works for 48 hours — kindly open and ` +
        `save your copy now, as it will stop working after that.` + portalLine;
      window.open(`https://wa.me/${intl}?text=${encodeURIComponent(desktopText)}`, "_blank");
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error("Could not prepare the WhatsApp message");
    }
  };

  // Lay-by / order ready: a WhatsApp nudge with what is still owing.
  const goodsReadyWhatsApp = async () => {
    const phone = (doc.customer_phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("This record has no phone number");
      return;
    }
    const code = String(doc.customer_country_code || "+61").replace(/\D/g, "");
    const intl = phone.startsWith(code) ? phone : `${code}${phone.replace(/^0+/, "")}`;
    const owing = Number(doc.balance || 0);
    let hours = "";
    try {
      const { data } = await api.get("/settings/branches");
      hours = data?.[doc.branch]?.hours_sentence || "";
    } catch {
      hours = "";
    }
    const text =
      `Hi ${doc.customer_name || "there"}, Dhunna Jewellers here. Good news — your order on ` +
      `${DOC_LABELS[doc.doc_type] || "invoice"} #${doc.doc_number} is ready to collect` +
      (owing > 0.005
        ? `. There is ${money(owing)} still to pay, which you can settle when you come in.`
        : `, and it is paid in full.`) +
      (hours ? ` Our ${doc.branch} store hours: ${hours}.` : "") +
      ` See you soon.`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("WhatsApp opened with the message ready to send");
  };

  const voidDocument = async () => {
    const reason = window.prompt(
      "Why is this record being voided? (kept on the permanent record)"
    );
    if (reason === null) return;
    try {
      const { data } = await api.put(`/documents/${id}/void`, { reason });
      setDoc({ ...doc, ...data });
      toast.success("Record voided — the trail is kept");
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not void this record"
      );
    }
  };

  const setLoanStatus = async (status) => {    try {
      await api.put(`/documents/${id}/loan-status`, { status });
      setDoc({ ...doc, loan: { ...doc.loan, status } });
      toast.success(`Loan marked ${status}`);
    } catch (e) {
      toast.error("Could not update loan status");
    }
  };

  if (!doc)
    return (
      <div className="py-20 text-center text-sm text-muted-foreground" data-testid="document-loading">
        Loading record…
      </div>
    );

  const isTax = doc.doc_type === "tax_invoice";
  const isPaidFull = Math.abs(doc.balance || 0) <= 0.005 && (doc.total_paid || 0) > 0;
  const inCredit = (doc.balance || 0) < -0.005;
  const statusLabel = isPaidFull
    ? "Paid in full"
    : inCredit
      ? `In credit ${money(-(doc.balance || 0))}`
      : `Balance due ${money(doc.balance)}`;
  const isLoan = doc.doc_type === "loan";
  const isBuying = doc.doc_type === "buying_contract";
  const info = branches[doc.branch] || {};
  const sections = terms[doc.doc_type] || [];
  const blankRows = Math.max(0, 8 - (doc.items?.length || 0));
  const payRows = (doc.payments || []).filter((p) => p.amount);
  const tradeRows = (doc.trade_ins || []).filter((t) => t.amount);

  return (
    <div className="fade-up" data-testid="document-view">
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/documents"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          data-testid="back-to-list"
        >
          <ArrowLeft className="h-4 w-4" /> Back to records
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          {doc.signed_by && (
            <span
              className="border border-[#166534] bg-[#F0FDF4] px-3 py-1.5 text-xs font-semibold text-[#166534]"
              data-testid="signed-badge"
            >
              Signed by {doc.signed_by}
              {doc.signed_method === "emailed" ? " (online)" : " (in store)"} ·{" "}
              {shopDateTime(doc.signed_at)}
            </span>
          )}
          {doc.paid_in_full && (
            <span
              className="border border-[#166534] bg-[#F0FDF4] px-3 py-1.5 text-xs font-semibold text-[#166534]"
              data-testid="paid-in-full-badge"
            >
              Paid in full
            </span>
          )}
          {doc.rounding_total > 0 && (
            <span
              className="no-print border border-[#8A6A22] bg-[#FBF8F2] px-3 py-1.5 text-xs font-semibold text-[#8A6A22]"
              data-testid="rounded-off-badge"
            >
              Rounded off {money(doc.rounding_total)} · not on invoice
            </span>
          )}
          {!doc.paid_in_full && doc.balance > 0.005 && (
            <span
              className="border border-[#9A3412] bg-[#FDF2EE] px-3 py-1.5 text-xs font-semibold text-[#9A3412]"
              data-testid="balance-due-badge"
            >
              Balance due {money(doc.balance)}
            </span>
          )}
          {doc.balance < -0.005 && (
            <span
              className="border border-[#166534] bg-[#F0FDF4] px-3 py-1.5 text-xs font-semibold text-[#166534]"
              data-testid="in-credit-badge"
            >
              In credit {money(-doc.balance)}
            </span>
          )}
          {doc.status === "void" && (
            <span
              className="border border-[#9A3412] bg-[#FDF2EE] px-3 py-1.5 text-xs font-semibold text-[#9A3412]"
              data-testid="void-trail"
            >
              Voided by {doc.voided_by} — {doc.void_reason}
            </span>
          )}
          {pdfLink && (
            <a
              href={pdfLink}
              target="_blank"
              rel="noreferrer"
              className="max-w-[260px] truncate border border-[#C5A059] bg-[#FBF8F2] px-3 py-1.5 text-xs text-[#8A6A22]"
              data-testid="pdf-share-link"
            >
              {pdfLink}
            </a>
          )}
          {isLoan && (
            <div className="flex items-center gap-2 text-sm" data-testid="loan-status-controls">
              <span className="text-muted-foreground">Status:</span>
              <span className="font-semibold uppercase" data-testid="loan-status-value">
                {doc.loan?.status}
              </span>
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => setLoanStatus("repaid")} data-testid="mark-repaid-button">
                Mark repaid
              </Button>
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => setLoanStatus("defaulted")} data-testid="mark-defaulted-button">
                Mark defaulted
              </Button>
            </div>
          )}
          {doc.customer_phone && (
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => navigate(`/customers/${(doc.customer_phone || "").replace(/\D/g, "")}`)}
              data-testid="view-customer-account-button"
            >
              <UserRound className="mr-1.5 h-4 w-4" /> Customer account
            </Button>
          )}
          {can("documents:amend") && doc.status !== "void" && !doc.signature_file_id && (
            <>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => setSignOpen(true)}
                data-testid="capture-signature-button"
              >
                <PenLine className="mr-1.5 h-4 w-4" /> Sign in store
              </Button>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={requestSignature}
                data-testid="request-signature-button"
              >
                <Send className="mr-1.5 h-4 w-4" /> Email to sign
              </Button>
            </>
          )}
          {can("documents:amend") && doc.status !== "void" && (
            <>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => navigate(`/documents/${id}/edit`)}
                data-testid="amend-document-button"
              >
                <Pencil className="mr-1.5 h-4 w-4" /> Amend
              </Button>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => {
                  setRenameName(doc.customer_name || "");
                  setRenamePhone(doc.customer_phone || "");
                  setRenameAddress(doc.customer_address || "");
                  setRenameReason(TRS_REASON);
                  setRenameOpen(true);
                }}
                data-testid="change-invoice-name-button"
              >
                <UserRound className="mr-1.5 h-4 w-4" /> Change details
              </Button>
              {doc.balance > 0.005 && (
                <Button
                  variant="outline"
                  className="rounded-none border-[#C5A059] text-[#8A6A22] hover:bg-[#FBF8F2]"
                  onClick={() => setPayFull(true)}
                  data-testid="mark-paid-full-button"
                >
                  <BadgeCheck className="mr-1.5 h-4 w-4" /> Mark as paid in full
                </Button>
              )}
              {!isBuying &&
                !isLoan &&
                (doc.balance > 0.005 ||
                  (doc.paid_in_full &&
                    round2((doc.total || 0) - (doc.trade_in_total || 0) - (doc.rounding_total || 0)) >
                      0.005)) && (
                  <Button
                    variant="outline"
                    className="rounded-none border-[#C5A059] text-[#8A6A22] hover:bg-[#FBF8F2]"
                    onClick={() => setRoundOff(true)}
                    data-testid="round-off-button"
                  >
                    <BadgeCheck className="mr-1.5 h-4 w-4" />{" "}
                    {doc.balance > 0.005 ? "Round off balance" : "Round off / write off"}
                  </Button>
                )}
            </>
          )}
          <Button variant="outline" className="rounded-none" onClick={emailToCustomer} disabled={emailing} data-testid="email-customer-button">
            <Mail className="mr-1.5 h-4 w-4" />
            {doc.emailed_to ? "Email again" : "Email to customer"}
          </Button>
          {doc.customer_phone && (
            <Button
              variant="outline"
              className="rounded-none border-[#1F6F43] text-[#1F6F43] hover:bg-[#F1FAF4]"
              onClick={sendWhatsApp}
              data-testid="whatsapp-customer-button"
            >
              <MessageCircle className="mr-1.5 h-4 w-4" /> Send on WhatsApp
            </Button>
          )}
          {doc.customer_phone && doc.status === "active" && !isBuying && !isLoan && (
            <Button
              variant="outline"
              className="rounded-none border-[#1F6F43] text-[#1F6F43] hover:bg-[#F1FAF4]"
              onClick={goodsReadyWhatsApp}
              data-testid="goods-ready-whatsapp-button"
            >
              <PackageCheck className="mr-1.5 h-4 w-4" /> Goods ready — WhatsApp
            </Button>
          )}
          {can("documents:void") && doc.status !== "void" && (
            <Button
              variant="outline"
              className="rounded-none border-destructive/40 text-destructive hover:bg-destructive/5"
              onClick={voidDocument}
              data-testid="void-document-button"
            >
              <Ban className="mr-1.5 h-4 w-4" /> Void
            </Button>
          )}
          <Button variant="outline" className="rounded-none" onClick={() => navigate(`/new/${doc.doc_type}?from=${id}`)} data-testid="reorder-button">
            <RotateCcw className="mr-1.5 h-4 w-4" /> Reorder
          </Button>
          <Button variant="outline" className="rounded-none" onClick={copyLink} data-testid="copy-pdf-link-button">
            <LinkIcon className="mr-1.5 h-4 w-4" /> Copy PDF link
          </Button>
          <Button variant="outline" className="rounded-none" onClick={printDoc} data-testid="print-button">
            <Printer className="mr-1.5 h-4 w-4" /> Print
          </Button>
          <Button className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]" onClick={download} disabled={busy} data-testid="download-pdf-button">
            <Download className="mr-1.5 h-4 w-4" /> Download PDF
          </Button>
          <div className="ml-auto flex flex-wrap items-center gap-2 border-l-2 border-[#9A3412]/40 pl-3" data-testid="internal-copy-group">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#9A3412]">Internal only</span>
            <Button variant="outline" className="rounded-none border-[#9A3412]/50 bg-[#FDF3EC] text-[#9A3412] hover:bg-[#F6E2D4]" onClick={downloadOffice} disabled={busy} data-testid="download-office-copy-button" title="Download the internal file copy — includes the customer ID photo. Not for the customer.">
              <UserRound className="mr-1.5 h-4 w-4" /> Office copy (ID)
            </Button>
            <Button variant="outline" className="rounded-none border-[#9A3412]/50 bg-[#FDF3EC] text-[#9A3412] hover:bg-[#F6E2D4]" onClick={printInternal} disabled={busy} data-testid="print-office-copy-button" title="Print the internal file copy that includes the customer ID photo. Not for the customer.">
              <Printer className="mr-1.5 h-4 w-4" /> Print ID copy
            </Button>
          </div>
        </div>
      </div>

      {emails.length > 0 && (
        <div className="no-print mt-6 border border-border bg-white" data-testid="email-history">
          <div className="border-b border-border px-4 py-2.5">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#8A6A22]">
              <Mail className="h-4 w-4" /> Email history
            </h3>
          </div>
          <ul className="divide-y divide-border">
            {emails.map((e, i) => (
              <li
                key={i}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
                data-testid={`email-history-row-${i}`}
              >
                <span className="font-medium" data-testid={`email-history-to-${i}`}>{e.to}</span>
                <span className="text-muted-foreground">{shopDateTime(e.sent_at)}</span>
                {e.attached ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-[#F1FAF4] px-2 py-0.5 text-xs font-semibold text-[#1F6F43]"
                    data-testid={`email-history-attached-${i}`}
                  >
                    <BadgeCheck className="h-3 w-3" /> PDF attached
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-[#FBF3E6] px-2 py-0.5 text-xs font-semibold text-[#8A6A22]"
                    data-testid={`email-history-linkonly-${i}`}
                  >
                    <LinkIcon className="h-3 w-3" /> Link only
                  </span>
                )}
                <span className="text-xs text-muted-foreground">by {e.sent_by}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {signOpen && (
        <div
          className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="signature-dialog"
        >
          <div className="w-full max-w-lg border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">
              Customer signature — #{doc.doc_number}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Hand the screen to {doc.customer_name} to sign. It prints on the document in place
              of the paper signature.
            </p>
            <div className="mt-5">
              <SignaturePad
                onSave={saveSignature}
                onCancel={() => setSignOpen(false)}
                saving={signSaving}
                testidPrefix="instore-signature"
              />
            </div>
          </div>
        </div>
      )}

      {payFull && (
        <div
          className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="pay-full-dialog"
        >
          <div className="w-full max-w-sm border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Record a payment</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Outstanding balance{" "}
              <span className="tabular font-semibold text-foreground">{money(doc.balance)}</span>.
              Card, Afterpay and Kuber providers take their own fee at their end — take
              only what is owed here.
            </p>            <div className="mt-4">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Amount (leave blank to settle in full)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={money(doc.balance)}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="mt-1.5 h-10 w-full border border-input px-3 text-right"
                data-testid="pay-amount-input"
              />
            </div>
            <div className="mt-4 space-y-2">
              {["CASH", "BANK TRANSFER", "PAYID", "CARD DEBIT", "CARD", "KUBER PAYID", "KUBER INSTALLMENTS", "AFTERPAY", "KLARNA", "ZIP PAY"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPayMethod(m)}
                  className={`w-full border px-4 py-2.5 text-left text-sm transition-colors duration-150 ${
                    payMethod === m
                      ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                      : "border-border hover:bg-muted"
                  }`}
                  data-testid={`pay-method-${m.toLowerCase().replace(" ", "-")}`}
                >
                  {m}
                </button>
              ))}
            </div>
            {(() => {
              const p = payPlan();
              return (
                <div
                  className="mt-4 border border-[#C5A059] bg-[#FBF8F2] p-3 text-sm"
                  data-testid="pay-breakdown"
                >
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Balance on invoice</span>
                    <span className="tabular">{money(p.outstanding)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {payMethod} fee, kept by them
                    </span>
                    <span className="tabular">{money(p.fee)}</span>
                  </div>
                  <div className="mt-1 flex justify-between border-t border-[#C5A059] pt-1 font-bold">
                    <span>Take from the customer</span>
                    <span className="tabular" data-testid="pay-charge-amount">{money(p.charge)}</span>
                  </div>
                  {p.left > 0.005 ? (
                    <p className="mt-2 text-xs font-semibold text-red-700" data-testid="pay-shortfall">
                      This leaves {money(p.left)} still owing. Take {money(p.settle)} to clear
                      it completely, or leave the amount box blank.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Takes this invoice to paid in full — nothing left owing.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="mt-5 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setPayFull(false)}
                data-testid="cancel-pay-full"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={settleBalance}
                data-testid="confirm-pay-full"
              >
                Record payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {roundOff && (
        <div
          className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="round-off-dialog"
        >
          <div className="w-full max-w-sm border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">
              {doc.balance > 0.005 ? "Round off the balance" : "Round off / write off"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {doc.balance > 0.005 ? (
                <>
                  <span className="tabular font-semibold text-foreground">{money(doc.balance)}</span>{" "}
                  is still owing. A round-off waives it so the invoice reads paid in full.
                </>
              ) : (
                <>
                  This invoice is already marked paid in full. Use this to record a few
                  dollars you chose not to collect — for example the customer paid $1,250 on
                  a $1,254 bill. The invoice itself does not change (it still shows the full
                  amount paid); the write-off is captured for your accountant only.
                </>
              )}{" "}
              It is kept on the record for your reconciliation and your accountant report,
              but is never printed on the customer invoice, and it does not change the price
              or the GST.
            </p>
            <div className="mt-4">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {doc.balance > 0.005
                  ? "Amount to round off (leave blank to clear the balance)"
                  : "Amount to write off"}
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={doc.balance > 0.005 ? money(doc.balance) : "0.00"}
                value={roundAmt}
                onChange={(e) => setRoundAmt(e.target.value)}
                className="mt-1.5 h-10 w-full border border-input px-3 text-right"
                data-testid="round-off-amount-input"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Amounts above $9.99 will ask you to confirm first.
              </p>
            </div>
            <div className="mt-5 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => {
                  setRoundOff(false);
                  setRoundAmt("");
                }}
                data-testid="cancel-round-off"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={applyRoundOff}
                data-testid="confirm-round-off"
              >
                Apply round-off
              </Button>
            </div>
          </div>
        </div>
      )}

      {renameOpen && (
        <div
          className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="rename-invoice-dialog"
        >
          <div className="w-full max-w-md border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">
              Change the customer details on #{doc.doc_number}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This record only — currently{" "}
              <span className="font-semibold text-foreground">{doc.customer_name}</span>. The
              invoice number, items and totals stay exactly as issued, and the customer account
              keeps its own details.
            </p>
            <div className="mt-4">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Name as it should print
              </label>
              <input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                className="mt-1.5 h-10 w-full border border-input px-3 font-sans"
                data-testid="rename-invoice-name-input"
              />
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Phone
                </label>
                <input
                  value={renamePhone}
                  onChange={(e) => setRenamePhone(e.target.value)}
                  className="mt-1.5 h-10 w-full border border-input px-3 font-sans"
                  data-testid="rename-invoice-phone-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Address
                </label>
                <input
                  value={renameAddress}
                  onChange={(e) => setRenameAddress(e.target.value)}
                  className="mt-1.5 h-10 w-full border border-input px-3 font-sans"
                  data-testid="rename-invoice-address-input"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reason (kept on the permanent trail)
              </label>
              <input
                value={renameReason}
                onChange={(e) => setRenameReason(e.target.value)}
                className="mt-1.5 h-10 w-full border border-input px-3 font-sans"
                data-testid="rename-invoice-reason-input"
              />
              <button
                type="button"
                onClick={() => setRenameReason(TRS_REASON)}
                className="mt-2 border border-[#C5A059] bg-[#FBF8F2] px-2.5 py-1 text-xs text-[#8A6A22] transition-colors duration-150 hover:bg-[#F5EEDF]"
                data-testid="rename-reason-trs-preset"
              >
                Use TRS reason
              </button>
            </div>
            <div className="mt-5 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setRenameOpen(false)}
                data-testid="cancel-rename-invoice"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={saveInvoiceName}
                disabled={renaming}
                data-testid="confirm-rename-invoice"
              >
                Save details
              </Button>
            </div>
          </div>
        </div>
      )}

      {doc.attachments?.length > 0 && (
        <div className="no-print mb-6 border border-border" data-testid="document-attachments">
          <div className="border-b border-border bg-[#F8F9FA] px-4 py-2.5 text-sm font-bold uppercase tracking-wider">
            Customer ID &amp; photos
          </div>
          <div className="flex flex-wrap gap-3 p-4">
            {doc.attachments.map((a, i) => (
              <button
                type="button"
                key={a.file_id}
                onClick={() =>
                  openDownload(`/files/${a.file_id}`).catch(() =>
                    toast.error("Could not open that file")
                  )
                }
                className="w-[130px] border border-border text-left transition-colors duration-150 hover:border-[#C5A059]"
                data-testid={`attachment-${i}`}
              >
                {a.content_type === "application/pdf" ? (
                  <div className="flex h-[95px] items-center justify-center bg-muted text-xs">
                    PDF document
                  </div>
                ) : (
                  <AuthedImage
                    fileId={a.file_id}
                    alt={a.filename || "ID photo"}
                    className="h-[95px] w-full object-cover"
                  />
                )}
                <div className="px-2 py-1 text-[10px] text-muted-foreground">
                  {a.size ? `${(a.size / 1024).toFixed(0)} KB` : a.kind}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {doc.amendments?.length > 0 && (
        <div className="no-print mb-6 border border-border" data-testid="amendment-trail">
          <div className="border-b border-border bg-[#F8F9FA] px-4 py-2.5 text-sm font-bold uppercase tracking-wider">
            Amendment history
          </div>
          <div className="divide-y divide-border text-sm">
            {doc.amendments.map((a, i) => (
              <div key={i} className="px-4 py-3" data-testid={`amendment-${i}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">{a.reason}</span>
                  <span className="text-xs text-muted-foreground">
                    {a.by} · {shopDateTime(a.at)}
                  </span>
                </div>
                {a.name_after ? (
                  <div className="mt-1 text-xs text-muted-foreground" data-testid={`amendment-name-${i}`}>
                    Details on this record:{" "}
                    {(a.fields_changed || ["name"])
                      .map(
                        (f) =>
                          `${f} ${(a.details_before || { name: a.name_before })[f] || "-"} → ${
                            (a.details_after || { name: a.name_after })[f] || "-"
                          }`
                      )
                      .join(" · ")}{" "}
                    (customer account unchanged)
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Total {money(a.before?.total)} → {money(a.after?.total)} · weight{" "}
                    {Number(a.before?.total_weight || 0).toFixed(3)} →{" "}
                    {Number(a.after?.total_weight || 0).toFixed(3)} gm
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoan && <div className="no-print mb-8"><LoanLedger docId={id} /></div>}

      {previewLoading && pages.length === 0 && !pdfBroken && (
        <div
          className="no-print mx-auto max-w-[210mm] animate-pulse border border-border bg-white p-10 text-center"
          data-testid="invoice-preview-loading"
        >
          <div className="mx-auto h-4 w-40 bg-[#EFE7D8]" />
          <div className="mx-auto mt-6 h-[420px] w-full bg-[#FBF8F2]" />
          <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">
            Bringing up the printed document on the letterhead…
          </p>
        </div>
      )}

      {pages.length > 0 && !pdfBroken && (
        <div className="mx-auto max-w-[210mm] space-y-4" data-testid="invoice-pdf-preview">
          {pages.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`Page ${i + 1}`}
              className="w-full border border-border bg-white shadow-sm"
              data-testid={`invoice-preview-page-${i + 1}`}
            />
          ))}
          <p className="no-print text-center text-xs text-muted-foreground">
            This is the actual printed document — what you see here is exactly what prints.
          </p>
          {pdfPreview && (
            <iframe
              ref={pdfFrame}
              title="print"
              src={pdfPreview}
              className="hidden h-0 w-0"
              aria-hidden="true"
            />
          )}
        </div>
      )}

      <div
        className={`print-area mx-auto max-w-[210mm] bg-white p-6 font-invoice text-[11px] leading-tight text-black${
          pdfBroken ? "" : " hidden"
        }`}
        data-testid="invoice-print-area"
      >
        <div className="print-page">
        <Brand abn={info.abn} />
        {doc.status === "void" && (
          <div
            className="mt-2 border border-[#9A3412] bg-[#FDF2EE] px-3 py-2 text-center text-[10px] font-bold uppercase text-[#9A3412]"
            data-testid="void-banner"
          >
            Voided — not a valid {TITLES[doc.doc_type]}
            {doc.void_reason ? ` | ${doc.void_reason}` : ""}
          </div>
        )}

        <div className="mt-2 flex items-center gap-3">
          <span className="h-[1.5px] flex-1 bg-[#C08A2E]" />
          <h2 className="text-center text-[15px] font-bold uppercase tracking-wide">
            {TITLES[doc.doc_type]}
          </h2>
          <span className="h-[1.5px] flex-1 bg-[#C08A2E]" />
        </div>
        {!isBuying && (doc.total_paid > 0 || doc.total > 0) && (
          <div className="mt-2 flex justify-end">
            <span
              className={`border px-4 py-1 text-[11px] font-bold uppercase ${
                isPaidFull || inCredit
                  ? "border-[#166534] text-[#166534]"
                  : "border-[#9A3412] text-[#9A3412]"
              }`}
              data-testid="print-payment-status"
            >
              {statusLabel}
            </span>
          </div>
        )}

        <div className="mt-3 flex flex-wrap justify-between gap-6">
          <div className="min-w-[250px] flex-1 space-y-0.5">
            <div className="font-bold uppercase">{isLoan || isBuying ? "Customer details -" : "Bill to -"}</div>
            {[
              ["CUSTOMER NAME:", doc.customer_name],
              ["CUSTOMER PHONE:", formatPhone(doc.customer_phone)],
              ["CUSTOMER ADDRESS:", doc.customer_address],
              ["EMAIL ADDRESS:", doc.customer_email],
              ...(isBuying
                ? [
                    ["DATE OF BIRTH:", doc.buying?.date_of_birth],
                    ["ID NUMBER:", doc.buying?.id_number],
                    ["STATE OF ISSUE:", doc.buying?.state_of_issue],
                    ["EXPIRY:", doc.buying?.expiry],
                    ["DEALER NAME:", doc.buying?.dealer_name],
                  ]
                : []),
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span className="w-[45%] shrink-0 font-bold uppercase">{k}</span>
                <span className="uppercase">{v || ""}</span>
              </div>
            ))}
          </div>

          <div className="w-[78mm] shrink-0">
            <div className="grid grid-cols-2 border-b border-black pb-0.5 text-center font-bold">
              <span>TIME</span>
              <span>DATE</span>
            </div>
            <div className="grid grid-cols-2 pt-0.5 text-center">
              <span>{doc.doc_time}</span>
              <span>{doc.doc_date}</span>
            </div>
            <div className="ml-auto mt-2 w-[42mm]">
              <div className="border-b border-black pb-0.5 text-center font-bold">
                {NUMBER_LABELS[doc.doc_type]}
              </div>
              <div className="pt-1 text-center" data-testid="print-doc-number">
                {doc.doc_number}
              </div>
              <div className="text-center font-bold">{doc.branch}</div>
            </div>
          </div>
        </div>

        {isLoan ? (
          <table className="mt-4 w-full border border-black">
            <tbody>
              {[
                ["INTEREST RATE:", `${doc.loan?.interest_rate}% PER MONTH`, "INTEREST PER MONTH:", money(doc.loan?.interest_per_month)],
                ["LOAN AMOUNT:", money(doc.loan?.loan_amount), "ID NUMBER:", doc.loan?.id_number || ""],
                ["MINIMUM:", `${doc.loan?.min_months} MONTH`, "MAXIMUM:", `${doc.loan?.max_months} MONTHS`],
                ["START DATE:", doc.loan?.start_date || "", "END DATE:", doc.loan?.end_date || ""],
                ["DESCRIPTION:", (doc.loan?.description || "").toUpperCase(), "WEIGHT:", doc.loan?.weight || ""],
              ].map((r, i) => (
                <tr key={i} className="border-b border-neutral-400 last:border-0">
                  <td className="w-[17%] border-r border-neutral-400 px-2 py-1.5 font-bold">{r[0]}</td>
                  <td className="w-[33%] border-r border-neutral-400 px-2 py-1.5">{r[1]}</td>
                  <td className="w-[17%] border-r border-neutral-400 px-2 py-1.5 font-bold">{r[2]}</td>
                  <td className="px-2 py-1.5">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            {isTax && (
              <table className="mt-3 w-full border border-black text-center" data-testid="print-gold-rates">
                <tbody>
                  <tr>
                    <td className="w-[20%] border-r border-black px-2 py-1 font-bold">GOLD RATES</td>
                    {KARATS.map((k) => (
                      <td key={k} className="w-[20%] border-r border-black px-2 py-1 last:border-0">
                        {k} - {Number(doc.gold_rates?.[k] || 0).toFixed(3)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            )}

            <table className="mt-1 w-full border border-black" data-testid="invoice-items-table">
              <thead>
                <tr className="border-b border-black text-center font-bold">
                  {!isTax && (
                    <th className="w-[6%] border-r border-black px-1 py-1.5">NO.</th>
                  )}
                  <th className="border-r border-black px-1 py-1.5">DESCRIPTION</th>
                  {isTax ? (
                    <>
                      <th className="w-[7%] border-r border-black px-1 py-1.5">GWT</th>
                      <th className="w-[7%] border-r border-black px-1 py-1.5">ST. WT</th>
                      <th className="w-[7%] border-r border-black px-1 py-1.5">NWT</th>
                      <th className="w-[9%] border-r border-black px-1 py-1.5">RATE</th>
                      <th className="w-[8%] border-r border-black px-1 py-1.5">S/C</th>
                      <th className="w-[6%] border-r border-black px-1 py-1.5">M/C%</th>
                      <th className="w-[8%] border-r border-black px-1 py-1.5">S/P</th>
                    </>
                  ) : (
                    <>
                      <th className="w-[12%] border-r border-black px-1 py-1.5">QTY</th>
                      <th className="w-[16%] border-r border-black px-1 py-1.5">PRICE</th>
                    </>
                  )}
                  <th className="w-[11%] px-1 py-1.5">{isTax ? "AMT." : "AMOUNT"}</th>
                </tr>
              </thead>
              <tbody>
                {doc.items?.map((it, i) => (
                  <tr key={i} className="border-b border-neutral-400 text-center">
                    {!isTax && (
                      <td className="border-r border-neutral-400 px-1 py-1.5">{i + 1}</td>
                    )}
                    <td className="border-r border-neutral-400 px-2 py-1.5 text-left uppercase">
                      {it.description}
                    </td>
                    {isTax ? (
                      <>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {it.gross_weight ? Number(it.gross_weight).toFixed(3) : ""}
                        </td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {it.less_weight ? Number(it.less_weight).toFixed(3) : ""}
                        </td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {it.weight ? Number(it.weight).toFixed(3) : ""}
                        </td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {Number(it.set_price) < 0 ? "" : money(it.rate)}
                        </td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {it.stone_cost ? money(it.stone_cost) : ""}
                        </td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {it.labour_pct ? `${Number(it.labour_pct)}%` : ""}
                        </td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">
                          {it.set_price
                            ? Number(it.set_price) < 0
                              ? `-${money(Math.abs(Number(it.set_price)))}`
                              : money(it.set_price)
                            : ""}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">{it.qty}</td>
                        <td className="tabular border-r border-neutral-400 px-1 py-1.5">{money(it.rate)}</td>
                      </>
                    )}
                    <td className="tabular px-1 py-1.5">
                      {Number(it.amount) < 0
                        ? `-${money(Math.abs(Number(it.amount)))}`
                        : money(it.amount)}
                    </td>
                  </tr>
                ))}
                {Array.from({ length: blankRows }).map((_, i) => (
                  <tr key={`b${i}`} className="border-b border-neutral-400">
                    <td className="border-r border-neutral-400 px-1 py-2">&nbsp;</td>
                    {isTax ? (
                      <>
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                      </>
                    ) : (
                      <>
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                        <td className="border-r border-neutral-400" />
                      </>
                    )}
                    <td />
                  </tr>
                ))}
                {doc.platform_fee > 0 && (
                  <tr className="border-t border-black">
                    <td colSpan={isTax ? 8 : 4} className="px-2 py-1.5 text-center italic">
                      Platform Handling Fee
                    </td>
                    <td className="tabular border-l border-black px-1 py-1.5 text-center italic" data-testid="print-platform-fee">
                      {money(doc.platform_fee)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {(payRows.length > 0 || tradeRows.length > 0) && (
              <table
                className="mt-2 w-[105mm] border border-black"
                data-testid="print-payment-table"
              >
                <tbody>
                  <tr className="border-b border-neutral-400">
                    <td className="px-2 py-1 font-bold uppercase" data-testid="print-payment-heading">
                      Payment
                    </td>
                    <td className="w-[30mm] border-l border-neutral-400" />
                  </tr>
                  {tradeRows.map((t, i) => (
                    <tr key={`t${i}`} className="border-b border-neutral-400" data-testid={`print-trade-in-${i}`}>
                      <td className="px-2 py-1 uppercase">
                        {t.description || "Old gold trading"}
                      </td>
                      <td className="tabular border-l border-neutral-400 px-2 py-1 text-center">
                        -{money(t.amount)}
                      </td>
                    </tr>
                  ))}
                  {payRows.map((p, i) => (
                    <tr key={`p${i}`} className="border-b border-neutral-400 last:border-0" data-testid={`print-payment-${i}`}>
                      <td className="px-2 py-1 uppercase">{p.label || p.method}</td>
                      <td className="tabular border-l border-neutral-400 px-2 py-1 text-center">
                        {money(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="mt-3 flex flex-wrap justify-between gap-6">
              <div />
              <table className="w-[65mm]">
                <tbody>
                  <tr className="border-b border-neutral-400">
                    <td className="py-1 text-right font-bold">SUBTOTAL:</td>
                    <td className="tabular py-1 text-right" data-testid="print-subtotal">{money(doc.subtotal)}</td>
                  </tr>
                  {!isBuying && (
                    <tr className="border-b border-neutral-400">
                      <td className="py-1 text-right font-bold">GST({doc.gst_rate}%):</td>
                      <td className="tabular py-1 text-right" data-testid="print-gst">{money(doc.gst_amount)}</td>
                    </tr>
                  )}
                  {doc.platform_fee > 0 && (
                    <tr className="border-b border-neutral-400">
                      <td className="py-1 text-right font-bold">PLATFORM HANDLING FEE:</td>
                      <td className="tabular py-1 text-right" data-testid="print-handling-fee">
                        {money(doc.platform_fee)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="py-1 text-right font-bold">
                      {isBuying ? "TOTAL:" : "TOTAL INC PRICE:"}
                    </td>
                    <td className="tabular py-1 text-right font-bold" data-testid="print-total">{money(doc.total)}</td>
                  </tr>
                  {doc.trade_in_total > 0 && (
                    <tr className="border-t border-neutral-400">
                      <td className="py-1 text-right font-bold">OLD GOLD TRADE-IN:</td>
                      <td className="tabular py-1 text-right" data-testid="print-trade-in-total">
                        -{money(doc.trade_in_total)}
                      </td>
                    </tr>
                  )}
                  {(doc.deposit > 0 || doc.total_paid > 0) && (
                    <>
                      {doc.deposit > 0 && (
                        <tr className="border-t border-neutral-400">
                          <td className="py-1 text-right font-bold">
                            DEPOSIT{doc.deposit_method ? ` (${doc.deposit_method})` : ""}:
                          </td>
                          <td className="tabular py-1 text-right">{money(doc.deposit)}</td>
                        </tr>
                      )}
                      <tr className="border-t border-neutral-400">
                        <td className="py-1 text-right font-bold">PAID:</td>
                        <td className="tabular py-1 text-right" data-testid="print-paid">{money(doc.total_paid)}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-right font-bold">
                          {isPaidFull ? "PAID IN FULL" : inCredit ? "IN CREDIT:" : "BALANCE DUE:"}
                        </td>
                        <td className="tabular py-1 text-right" data-testid="print-balance">
                          {money(inCredit ? -(doc.balance || 0) : doc.balance)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="mt-3 space-y-0.5">
          {doc.payment_method &&
            !(doc.payments || []).some((p) => p.amount) &&
            (doc.balance || 0) <= 0.005 && (
              <div>
                <span className="font-bold">PAID BY: </span>
                {doc.payment_method}
              </div>
            )}
          {doc.wear_tear_explained && (
            <div className="font-bold" data-testid="print-wear-tear">
              WEAR AND TEAR EXPLAINED PROPERLY TO THE CUSTOMER.
            </div>
          )}
          {doc.notes && (
            <div>
              <span className="font-bold">NOTE: </span>
              {doc.notes}
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-6">
          <p className="max-w-[105mm] text-justify text-[9px] leading-snug">
            <span className="font-bold">I/WE {doc.customer_name?.toUpperCase()}</span>{" "}
            {ACK[doc.doc_type]}
          </p>
          <div className="w-[70mm] space-y-1">
            <div className="flex items-end gap-2">
              <span className="font-bold">SIGN:</span>
              <span className="flex-1 border-b border-black text-center">
                <img
                  src={`/brand/signature-${doc.branch}.png`}
                  alt="Authorised signature"
                  className="mx-auto h-12 w-auto object-contain"
                  data-testid="invoice-signature"
                />
              </span>
            </div>
            {doc.signature_file_id && (
              <div className="flex items-end gap-2">
                <span className="font-bold">CUSTOMER SIGN:</span>
                <span className="flex-1 border-b border-black text-center">
                  <AuthedImage
                    fileId={doc.signature_file_id}
                    alt="Customer signature"
                    className="mx-auto h-12 w-auto object-contain"
                    testid="customer-signature"
                  />
                </span>
              </div>
            )}
          </div>
        </div>
        {doc.signed_by && (
          <p className="mt-1 text-right text-[8px] uppercase" data-testid="signed-line">
            Signed by {doc.signed_by}
            {doc.signed_method === "emailed" ? " (online)" : " (in store)"}
          </p>
        )}

        <StoreFooter entity={info.entity_name} footer={footer} />
        </div>

        {sections.length > 0 && (
          <div className="print-page mt-8 break-before-page" data-testid="printed-terms">
            <Brand abn={info.abn} />
            <div className="mt-2 h-[1.5px] w-full bg-[#C08A2E]" />
            <h2 className="mt-3 text-center text-[15px] font-bold uppercase">
              Terms and Conditions
            </h2>
            <h3 className="text-center text-[15px] font-bold uppercase">
              of {info.entity_name}
            </h3>
            <div className="mt-2 flex justify-center">
              <span className="bg-[#C08A2E] px-6 py-1 text-[10px] font-bold uppercase text-white">
                Return and Exchange Policy
              </span>
            </div>
            <p className="mt-2 text-center text-[9.5px] font-bold uppercase">
              In this return and exchange policy, the expressions "we", "us" and "our" refer to{" "}
              {info.entity_name}
              <br />
              ABN: {info.abn} AND ACN: {info.acn}.
            </p>
            <div className="mt-3 space-y-1.5">
              {sections.map((section, i) => (
                <div key={i}>
                  <div className="text-[9px] font-bold uppercase">{section.title}</div>
                  <ul className="list-disc pl-4 text-[8px] leading-snug">
                    {(section.points || []).map((p, j) => (
                      <li key={j}>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-3 border border-[#9A3412] px-3 py-1.5 text-center text-[9px] font-bold text-[#9A3412]">
              IMPORTANT NOTE: RETURN OR EXCHANGE REQUESTS MUST BE MADE WITHIN 24 HOURS OF
              RECEIVING THE PRODUCT.
            </div>
            <StoreFooter entity={info.entity_name} footer={footer} />
          </div>
        )}
      </div>
    </div>
  );
}
